import { useEffect } from "react";
import { accentBg } from "../utils/accent";

/**
 * Política de Privacidade — AutEM
 *
 * Documento LGPD-compliant que detalha coleta, uso, compartilhamento e
 * proteção de dados pessoais tratados pela plataforma.
 * Página pública, linkada a partir do login.
 */
export default function PrivacyPage() {
  useEffect(() => {
    document.title = "Política de Privacidade, AutEM";
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/login" className="inline-flex items-center gap-2">
            <img src="/logo_light.png" alt="AutEM" className="h-8 object-contain" />
          </a>
          <a
            href="/login"
            className="text-sm font-semibold hover:underline"
            style={{ color: "var(--accent)" }}
            data-testid="privacy-back-login"
          >
            ← Voltar ao login
          </a>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <p className="text-xs uppercase tracking-widest font-semibold text-gray-500 mb-2">
          Documento legal
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Política de Privacidade
        </h1>
        <p className="text-gray-500 text-sm">
          Última atualização: 01 de fevereiro de 2026 · Versão 1.0
        </p>
        <div className="mt-4 h-1 w-16 rounded-full" style={accentBg()} />
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 pb-24 prose prose-slate">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-10 shadow-sm text-[15px] leading-relaxed text-gray-700 space-y-8">

          <section>
            <p className="text-sm text-gray-500 border-l-4 pl-4" style={{ borderColor: "var(--accent)" }}>
              A privacidade e a proteção de dados pessoais são compromissos fundamentais do{" "}
              <strong className="text-gray-900">AutEM</strong>. Esta Política descreve como coletamos,
              utilizamos, armazenamos, compartilhamos e protegemos os dados pessoais tratados na
              plataforma, em conformidade com a Lei Geral de Proteção de Dados Pessoais
              (Lei nº 13.709/2018 – LGPD) e demais legislações aplicáveis.
            </p>
          </section>

          <Section title="1. Definições">
            <p><strong>1.1.</strong> &ldquo;Dados Pessoais&rdquo;: qualquer informação relacionada a
              pessoa natural identificada ou identificável.</p>
            <p><strong>1.2.</strong> &ldquo;Titular&rdquo;: a pessoa natural a quem se referem os
              dados pessoais.</p>
            <p><strong>1.3.</strong> &ldquo;Controlador&rdquo;: pessoa a quem competem as decisões
              sobre o tratamento (em regra, o Cliente Contratante do AutEM).</p>
            <p><strong>1.4.</strong> &ldquo;Operador&rdquo;: pessoa que realiza o tratamento em nome
              do Controlador (o AutEM, na maioria das operações).</p>
            <p><strong>1.5.</strong> &ldquo;Tratamento&rdquo;: toda operação realizada com dados
              pessoais, tais como coleta, uso, acesso, armazenamento, transmissão, eliminação, etc.</p>
          </Section>

          <Section title="2. Quem somos e nosso papel">
            <p><strong>2.1.</strong> O AutEM é uma plataforma SaaS multi-tenant de gestão de
              serviços de campo. Atuamos como <strong>Operador</strong> dos dados pessoais que os
              nossos Clientes (empresas contratantes) inserem na plataforma, e como{" "}
              <strong>Controlador</strong> dos dados de conta e de contato dos nossos próprios
              usuários administrativos.</p>
            <p><strong>2.2.</strong> Encarregado (DPO):{" "}
              <a href="mailto:dpo@satellitus.com" style={{ color: "var(--accent)" }}>
                dpo@satellitus.com
              </a>.</p>
          </Section>

          <Section title="3. Dados que coletamos">
            <p><strong>3.1. Dados de conta e acesso:</strong> nome, e-mail, telefone, cargo,
              credenciais de acesso, logs de login.</p>
            <p><strong>3.2. Dados operacionais inseridos pelo Cliente:</strong> informações sobre
              pessoas (colaboradores, fornecedores, clientes finais), veículos, serviços, rotas,
              telemetria, geolocalização (quando habilitada), documentos, mídias e demais registros
              necessários à operação.</p>
            <p><strong>3.3. Dados de uso e telemetria da plataforma:</strong> endereço IP, tipo de
              dispositivo, navegador, timestamps, páginas acessadas, ações realizadas.</p>
            <p><strong>3.4. Dados de suporte e comunicação:</strong> conteúdo de tickets, e-mails,
              chats e chamadas com o time de suporte.</p>
            <p><strong>3.5. Cookies e tecnologias similares:</strong> utilizamos cookies estritamente
              necessários (autenticação, preferências) e cookies analíticos anônimos. Cookies de
              publicidade não são utilizados.</p>
          </Section>

          <Section title="4. Bases legais e finalidades">
            <p>Tratamos dados pessoais com base nas seguintes hipóteses da LGPD (art. 7º):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Execução de contrato</strong>, para prestar o serviço contratado (todas
                as funcionalidades operacionais).</li>
              <li><strong>Cumprimento de obrigação legal ou regulatória</strong>, retenção de logs
                de acesso, comunicação de incidentes, atendimento a autoridades.</li>
              <li><strong>Legítimo interesse</strong>, segurança da plataforma, prevenção a
                fraudes, melhorias e analytics agregados.</li>
              <li><strong>Consentimento</strong>, quando aplicável, para comunicações de marketing
                (opt-in explícito) ou funcionalidades opcionais (ex.: geolocalização precisa).</li>
            </ul>
          </Section>

          <Section title="5. Compartilhamento de dados">
            <p><strong>5.1.</strong> Não vendemos dados pessoais. Compartilhamos dados apenas nas
              hipóteses a seguir:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Sub-operadores contratados</strong> (infraestrutura de nuvem, mapas,
                mensageria, IA), sob contrato com cláusulas de proteção de dados.</li>
              <li><strong>Autoridades competentes</strong>, mediante ordem judicial ou requisição
                legal.</li>
              <li><strong>No âmbito de reorganização societária</strong>, mantendo os mesmos níveis
                de proteção.</li>
            </ul>
            <p><strong>5.2. Principais parceiros técnicos:</strong> provedores de nuvem
              (armazenamento e processamento), provedores de mapas e roteirização, provedores de
              telemetria/rastreamento e provedores de modelos de IA. A lista atualizada pode ser
              solicitada ao DPO.</p>
            <p><strong>5.3. Transferência internacional:</strong> alguns dados podem ser
              processados em data centers localizados fora do Brasil, sempre com garantias
              contratuais adequadas e em conformidade com a LGPD.</p>
          </Section>

          <Section title="6. Retenção e eliminação">
            <p><strong>6.1.</strong> Os dados são mantidos pelo tempo necessário ao cumprimento das
              finalidades e das obrigações legais.</p>
            <p><strong>6.2.</strong> Após o término do contrato com o Cliente, os Dados do Cliente
              permanecem disponíveis para exportação por <strong>30 dias</strong>, sendo em seguida
              eliminados ou anonimizados.</p>
            <p><strong>6.3.</strong> Logs de auditoria e registros exigidos por lei
              (ex.: Marco Civil da Internet, art. 15) são mantidos pelos prazos legais mínimos.</p>
          </Section>

          <Section title="7. Direitos dos titulares">
            <p>Nos termos do art. 18 da LGPD, o titular pode solicitar, gratuitamente e a qualquer
              tempo:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Confirmação da existência de tratamento</li>
              <li>Acesso aos dados</li>
              <li>Correção de dados incompletos, inexatos ou desatualizados</li>
              <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em
                desconformidade</li>
              <li>Portabilidade dos dados</li>
              <li>Eliminação dos dados tratados com base no consentimento</li>
              <li>Informação sobre entidades com as quais compartilhamos dados</li>
              <li>Informação sobre a possibilidade de não fornecer consentimento e suas
                consequências</li>
              <li>Revogação do consentimento</li>
            </ul>
            <p>Para exercer seus direitos, envie e-mail para{" "}
              <a href="mailto:dpo@satellitus.com" style={{ color: "var(--accent)" }}>
                dpo@satellitus.com
              </a>. Responderemos no prazo de até 15 dias úteis.</p>
            <p className="text-sm text-gray-500 italic">
              Titulares vinculados a Clientes Contratantes (colaboradores, fornecedores, clientes
              finais) devem, primeiramente, contactar o próprio Controlador (a empresa
              Contratante), que é a responsável primária pelas decisões sobre o tratamento.
            </p>
          </Section>

          <Section title="8. Segurança da informação">
            <p><strong>8.1.</strong> Adotamos medidas técnicas e administrativas apropriadas para
              proteger os dados, incluindo:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Criptografia em trânsito (TLS 1.2+) e em repouso</li>
              <li>Controle de acesso baseado em papéis (RBAC) e princípio do menor privilégio</li>
              <li>Autenticação forte e políticas de senha</li>
              <li>Logs de auditoria e monitoramento contínuo</li>
              <li>Backups automatizados com retenção definida</li>
              <li>Isolamento de dados por tenant (multi-tenant seguro)</li>
              <li>Testes de segurança periódicos e políticas de correção de vulnerabilidades</li>
            </ul>
            <p><strong>8.2. Incidentes:</strong> em caso de incidente de segurança que possa
              acarretar risco relevante aos titulares, comunicaremos a ANPD e os titulares
              afetados nos termos do art. 48 da LGPD.</p>
          </Section>

          <Section title="9. Menores">
            <p>A plataforma não é destinada a menores de 18 anos. Não coletamos intencionalmente
              dados de crianças e adolescentes. Caso identifiquemos coleta indevida, os dados
              serão eliminados prontamente.</p>
          </Section>

          <Section title="10. Alterações desta Política">
            <p>Esta Política pode ser atualizada a qualquer tempo. Publicaremos a versão vigente
              nesta página e comunicaremos alterações relevantes pelos canais oficiais. O uso
              continuado da Plataforma após a publicação implica ciência das alterações.</p>
          </Section>

          <Section title="11. Contato">
            <p>Dúvidas, solicitações ou reclamações relacionadas ao tratamento de dados pessoais:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Encarregado (DPO): <a href="mailto:dpo@satellitus.com" style={{ color: "var(--accent)" }}>dpo@satellitus.com</a></li>
              <li>Suporte: <a href="mailto:suporte@satellitus.com" style={{ color: "var(--accent)" }}>suporte@satellitus.com</a> (Seg-Sex, 9h às 18h)</li>
              <li>Institucional: <a href="mailto:contato@satellitus.com" style={{ color: "var(--accent)" }}>contato@satellitus.com</a></li>
            </ul>
            <p className="text-sm text-gray-500 mt-2">
              O titular também pode registrar reclamação junto à Autoridade Nacional de Proteção
              de Dados (ANPD):{" "}
              <a href="https://www.gov.br/anpd" target="_blank" rel="noreferrer"
                 style={{ color: "var(--accent)" }}>
                gov.br/anpd
              </a>.
            </p>
          </Section>

          <div className="pt-4 mt-8 border-t border-gray-200 text-xs text-gray-400 text-center">
            © {new Date().getFullYear()} AutEM Tecnologia · Todos os direitos reservados
          </div>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-bold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-2 text-[14.5px]">{children}</div>
    </section>
  );
}
